import { useState, useEffect, useCallback, useRef, useMemo, createContext, useContext } from 'react'
import { supabase, recoveryMode, clearRecoveryMode } from '../lib/supabase'
import { setUser as setSentryUser } from '../lib/sentry'
import { clearImpersonator } from '../lib/impersonation'
import { clearActiveBrandSelection, clearActiveAccountSelection } from './useAccount'
import { invalidateBrandAccess } from '../lib/brandAccess'
import type { Session, User } from '@supabase/supabase-js'
import type { ReactNode } from 'react'

// Block admin accounts from the brand portal. Checked server-side via the
// is_admin_email RPC (reads app_metadata.is_admin in auth.users) before any
// sign-in or sign-up attempt.
async function assertNotAdmin(email: string) {
  const { data: isAdmin, error } = await supabase.rpc('is_admin_email', { input_email: email })
  if (error) throw error
  if (isAdmin) {
    throw new Error('You are not authorized for this role.')
  }
}

// Only allow brand accounts into the brand portal. Checked server-side via
// is_non_brand_email RPC (reads user_metadata.role in auth.users) before any
// sign-in or sign-up attempt. New accounts (email not yet registered) pass through.
async function assertBrandRole(email: string) {
  const { data: isNonBrand, error } = await supabase.rpc('is_non_brand_email', { input_email: email })
  if (error) throw error
  if (isNonBrand) {
    throw new Error('You are not authorized for this role.')
  }
}

const OAUTH_ERROR_KEY = 'bmc_oauth_error'

// Called once at app boot (before any router redirect). Reads error_description
// from the URL, stashes it in sessionStorage, then cleans the URL immediately
// so the user never sees the raw params. The Login page reads from sessionStorage.
function captureOAuthErrorFromUrl() {
  const tryParse = (search: string) => {
    const params = new URLSearchParams(search.startsWith('#') ? search.slice(1) : search)
    if (params.get('error')) {
      return decodeURIComponent(params.get('error_description') ?? 'Authentication error')
        .replace(/^\d+:\s*/, '') // strip leading status code e.g. "403: "
    }
    return null
  }
  const msg = tryParse(window.location.search) ?? tryParse(window.location.hash)
  if (msg) {
    sessionStorage.setItem(OAUTH_ERROR_KEY, msg)
    history.replaceState(null, '', window.location.pathname)
  }
}

// Run immediately at module load — before any component mounts or router redirects.
captureOAuthErrorFromUrl()

/**
 * Auth state for the whole app, resolved ONCE.
 *
 * This used to be a plain hook, and all twelve call sites got their own copy of
 * it: twelve onAuthStateChange subscriptions and twelve getSession() calls, each
 * maintaining a separate-but-identical piece of state, with more added on every
 * navigation. Beyond the redundant work, concurrent getSession() calls on an
 * expired token contend for supabase-js's auth lock — the "Navigator LockManager"
 * noise this project already filters out in Sentry.
 *
 * useAuth() below now reads a context instead, so the API every caller sees is
 * unchanged; only the number of subscriptions is.
 *
 * oauthError benefits from that directly. Its initialiser READS AND CLEARS the
 * sessionStorage handoff, so under the old plain hook only whichever instance
 * mounted first ever saw the message — which is why AcceptInvite takes it as a
 * prop from App rather than calling useAuth() itself. One instance now, so
 * there is no longer a race to be first.
 */
function useAuthState() {
  const [session, setSession] = useState<Session | null>(null)
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [isRecovery, setIsRecovery] = useState(recoveryMode.value)
  const [oauthError, setOauthError] = useState<string | null>(() => {
    const stored = sessionStorage.getItem(OAUTH_ERROR_KEY)
    if (stored) sessionStorage.removeItem(OAUTH_ERROR_KEY)
    return stored
  })
  // Ref so the onAuthStateChange closure always reads the latest value
  // without needing to re-register the listener
  const recoveryRef = useRef(recoveryMode.value)

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === 'PASSWORD_RECOVERY') {
          // Handles both direct tab (hash) and cross-tab (BroadcastChannel)
          recoveryRef.current = true
          setIsRecovery(true)
          setUser(null)
          setSession(null)
          setLoading(false)
        } else if (event === 'USER_UPDATED') {
          // Only reachable, non-guard-triggered case: password was just reset
          // (Login/Team's updateUser({password}) calls, always inside recovery
          // mode) — allow normal sign-in now. The Google guard below (step 4)
          // also calls updateUser (to stamp role:'brand'), which fires this
          // same USER_UPDATED event while its own IIFE is still awaiting that
          // call — recoveryRef.current is false for that case, so skip here
          // and let the guard finish and set loading itself; setting it false
          // now would flash the Login page for an instant before the guard
          // admits the user.
          if (recoveryRef.current) {
            recoveryRef.current = false
            clearRecoveryMode()
            setIsRecovery(false)
            setLoading(false)
          }
        } else if (!recoveryRef.current) {
          setIsRecovery(false)
          // Google-ONLY account that isn't an established brand account yet:
          // assign the brand role (the portal-specific half of the OAuth
          // rules — see the google_oauth_brand_guard hook, which owns the
          // portal-agnostic half: admin blocking and the "this email already
          // has a password account" rejection). Hold loading until it
          // resolves so the app never flashes protected content mid-check.
          //
          // Gated on app_metadata.providers (plural — every linked identity),
          // NEVER app_metadata.provider (singular). The singular field is
          // stamped once at account creation and never updates when another
          // identity links later, so an email/password account stays
          // provider:'email' forever, even during a Google sign-in — a gate
          // on it silently skipped this whole block for those accounts.
          //
          // The !includes('email') half matters: it scopes this to accounts
          // whose ONLY identity is Google, so a password sign-in on an
          // email account is never touched here (a missing role is ambiguous
          // — creators have no role in metadata either, so acting on an
          // email-identity account could mislabel one as 'brand').
          const providers: string[] = session?.user?.app_metadata?.providers ?? []
          const existingRole = session?.user?.user_metadata?.role
          const isGoogleOnly = providers.includes('google') && !providers.includes('email')
          if (session && isGoogleOnly && existingRole !== 'brand') {
            const confirmedSession = session
            ;(async () => {
              // Defense in depth — the hook blocks admins server-side before
              // a token is ever issued, so this should be unreachable.
              const { data: isAdmin } = await supabase.rpc('is_admin_email', {
                input_email: confirmedSession.user.email ?? '',
              })
              if (isAdmin) {
                await supabase.auth.signOut()
                setOauthError('You are not authorized for this role.')
                setLoading(false)
                return
              }

              // An explicit non-brand role (e.g. a creator signing in with
              // Google once creator-app supports it) has no business in the
              // brand portal.
              if (existingRole && existingRole !== 'brand') {
                await supabase.auth.signOut()
                setOauthError('You are not authorized for this role.')
                setLoading(false)
                return
              }

              // No role yet — a brand-new Google sign-up on this portal.
              // Stamp role:'brand', mirroring signUp()'s options.data.role on
              // the email/password path (Google OAuth can't carry metadata at
              // account-creation time, so this is the earliest we can set it).
              let admittedUser = confirmedSession.user
              const { data, error } = await supabase.auth.updateUser({ data: { role: 'brand' } })
              if (!error && data.user) admittedUser = data.user
              setSession(confirmedSession)
              setUser(admittedUser)
              setSentryUser({ id: admittedUser.id, email: admittedUser.email })
              setLoading(false)
            })()
            return // stay loading until the async IIFE above settles
          }
          setSession(session)
          setUser(session?.user ?? null)
          setSentryUser(session?.user ? { id: session.user.id, email: session.user.email } : null)
          setLoading(false)
        } else {
          // SIGNED_IN fired right after PASSWORD_RECOVERY — stay in recovery mode
          setLoading(false)
        }
      }
    )

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!recoveryRef.current) {
        // Skip a Google-only session with no brand role yet — the
        // onAuthStateChange handler above runs the role check and sets the
        // session once it resolves. Setting it here first would bypass that
        // and flash protected content before the check finishes.
        const providers: string[] = session?.user?.app_metadata?.providers ?? []
        if (
          providers.includes('google') &&
          !providers.includes('email') &&
          session?.user?.user_metadata?.role !== 'brand'
        ) return
        setSession(session)
        setUser(session?.user ?? null)
        setSentryUser(session?.user ? { id: session.user.id, email: session.user.email } : null)
        setLoading(false)
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  const signIn = useCallback(async (email: string, password: string) => {
    await assertNotAdmin(email)
    await assertBrandRole(email)
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
  }, [])

  const signUp = useCallback(async (email: string, password: string) => {
    await assertNotAdmin(email)
    await assertBrandRole(email)
    const { error } = await supabase.auth.signUp({ email, password, options: { data: { role: 'brand' } } })
    if (error) throw error
  }, [])

  const signOut = useCallback(async () => {
    // End any impersonation so a subsequent normal login in this tab isn't
    // mis-attributed to the admin.
    clearImpersonator()
    // Drop the persisted active-brand selection so the NEXT user to sign in on
    // this browser doesn't inherit a stale "inside brand X" state (which would
    // e.g. keep a fresh agency out of its lobby / show a phantom brand).
    clearActiveBrandSelection()
    // Same reasoning for the account choice: a user in two agencies leaves a
    // persisted account id behind, and the next person to sign in on this browser
    // must not inherit it (pickAccount would find no matching row and fall back,
    // but only after resolving against someone else's selection).
    clearActiveAccountSelection()
    // Drop the shared brand_user_access rows so nothing cached against the
    // outgoing user can be read after the session ends.
    invalidateBrandAccess()
    await supabase.auth.signOut()
    // No hard reload here: signOut() above already fires onAuthStateChange,
    // which flips `user`/`session` to null and instantly unmounts
    // AuthenticatedApp in favor of <Login/> (App.tsx's catch-all route) —
    // AuthenticatedApp fully unmounting is what drops any brand/account state.
    // A window.location.replace('/') used to run here too, which forced a full
    // page reload on top of that already-instant transition: it painted Login
    // once from the reactive update, then again after the reload's own loading
    // spinner — a visible login->loading->login flicker. history.replaceState
    // just resets the URL bar (in case sign-out happened from a nested route
    // like /campaigns) without re-triggering any of that.
    window.history.replaceState(null, '', '/')
  }, [])

  const getToken = useCallback(async () => {
    const { data } = await supabase.auth.getSession()
    return data.session?.access_token ?? null
  }, [])

  // Memoised: this is a context value now, so a fresh object each render would
  // re-render every consumer on any unrelated parent render.
  return useMemo(
    () => ({ session, user, loading, isRecovery, oauthError, signIn, signUp, signOut, getToken }),
    [session, user, loading, isRecovery, oauthError, signIn, signUp, signOut, getToken],
  )
}

export type AuthState = ReturnType<typeof useAuthState>

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const value = useAuthState()
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  // A hard failure, not a silent fallback: a component rendered outside the
  // provider would otherwise quietly get a permanently-null user and read as
  // "signed out", which is exactly the kind of bug that reaches production.
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>')
  return ctx
}
